'use client';

import { loginWithOAuth } from '@adornis/authentication/oauth-client/client/loginWithOAuth.js';
import { logger } from '@adornis/base/logging.js';
import { currentUser, subscribeCurrentUser } from '@adornis/users/client/currentUser.js';
import { login } from '@adornis/users/client/loginFunctions.js';
import { AdornisUser, registerUser } from '@adornis/users/db/a-user.js';
import { ArrowLeft, CheckCircle2, X } from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next'; // <-- Import für Übersetzung
import { claimGuestConversationMutation } from '../../../db/api/claim-guest-conversation.js';
import { createStudentMutation } from '../../../db/api/create-student.js';
import { createTeacherMutation } from '../../../db/api/create-teacher.js';
import { requestPasswordResetMutation } from '../../../db/api/password-reset.js';
import { type Subject, ALL_SUBJECTS } from '../../../db/subjects.js';
import GoogleIconPath from '../../assets/google.svg';
import { Button } from '../../components/ui/button.js';
import LabeledInput, { MultiSelectDropdown, validateInput } from '../../components/ui/labeled-inputs.js';
import { clearGuestConversationId, clearGuestMessageCount, getGuestConversationId } from '../../lib/guest-user-id.js';

interface AuthModalProps {
  onContinueAsGuest?: () => void;
  initialMode?: 'login' | 'signup';
  onAuthSuccess?: () => void;
  /** Preselects the sign-up role toggle, e.g. 'student' when opened from the `/join` class-code landing page. */
  defaultRole?: 'teacher' | 'student';
}

export function AuthModal({
  onContinueAsGuest,
  initialMode = 'login',
  onAuthSuccess,
  defaultRole = 'teacher',
}: AuthModalProps) {
  const { t, i18n } = useTranslation();

  logger.info(`🔥 AUTH MODAL WURDE GERENDERT!`);
  const [isLogin, setIsLogin] = useState(initialMode === 'login');
  const [showValidation, setShowValidation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);

  const [showWaitlist, setShowWaitlist] = useState(false);
  const [waitlistSuccess] = useState(false);

  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);

  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  const [formData, setFormData] = useState({
    role: defaultRole,
    firstName: '',
    lastName: '',
    school: '',
    subjects: [] as Subject[],
    email: '',
    password: '',
    confirmPassword: '',
  });

  const requiredFields = {
    firstName: true,
    lastName: true,
    school: true,
    subjects: true,
    email: true,
    password: true,
    confirmPassword: true,
  };

  const handleSignUpChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubjectToggle = (subject: Subject) => {
    setFormData(prev => {
      const nextSubjects = prev.subjects.includes(subject)
        ? prev.subjects.filter(s => s !== subject)
        : [...prev.subjects, subject];
      return { ...prev, subjects: nextSubjects };
    });
  };

  const hasValidationErrors = () => {
    return (
      (requiredFields.firstName && !validateInput(formData.firstName, 'text')) ||
      (requiredFields.lastName && !validateInput(formData.lastName, 'text')) ||
      (requiredFields.school && !validateInput(formData.school, 'text')) ||
      // Students aren't asked to pick subjects at signup — they add them freely later in their
      // profile, since they aren't restricted to a fixed subject list the way teachers are.
      (formData.role === 'teacher' && requiredFields.subjects && !validateInput(formData.subjects, 'multiselect')) ||
      (requiredFields.email && !validateInput(formData.email, 'email')) ||
      (requiredFields.password && !validateInput(formData.password, 'password')) ||
      (requiredFields.confirmPassword && !validateInput(formData.confirmPassword, 'password'))
    );
  };

  const toggleMode = () => {
    setIsLogin(!isLogin);
    setAuthError(null);
    setShowValidation(false);
    setShowForgotPassword(false);
  };

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError(null);
    setIsSubmitting(true);

    const guestConvId = getGuestConversationId();
    // Damit der Nutzer nach dem Login in seiner Gast-Konversation bleibt (statt aufs Dashboard zu
    // landen), reichen wir deren ID an page.tsx weiter. Vor login() setzen, damit der Schlüssel da
    // ist, wenn das currentUser-Observable emittiert.
    try {
      if (guestConvId) localStorage.setItem('claimedConversationId', guestConvId);
      else localStorage.removeItem('claimedConversationId');
    } catch (e) {}

    try {
      await login(loginEmail, loginPassword);
      await subscribeCurrentUser();

      const userId = await new Promise<string>(resolve => {
        const sub = currentUser().subscribe(u => {
          const id = (u as any)?._id || (u as any)?.id;
          if (id) {
            sub.unsubscribe();
            resolve(id);
          }
        });
      });

      if (guestConvId && userId) {
        try {
          await claimGuestConversationMutation(guestConvId, userId)();
          clearGuestConversationId();
        } catch (claimErr) {
          logger.error(claimErr, `❌ Fehler bei Migration:`);
        }
      }

      onAuthSuccess?.();
    } catch (err) {
      try {
        localStorage.removeItem('claimedConversationId');
      } catch (e) {}
      setAuthError(t('AuthModal.loginError', { defaultValue: 'E-Mail oder Passwort ist falsch.' }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSignUpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setShowValidation(true);
    setAuthError(null);

    if (hasValidationErrors()) return;

    if (formData.password !== formData.confirmPassword) {
      setAuthError(t('AuthModal.passwordMismatch', { defaultValue: 'Die Passwörter stimmen nicht überein.' }));
      return;
    }

    setIsSubmitting(true);

    const guestConvId = getGuestConversationId();
    // Nach der Registrierung in der Gast-Konversation bleiben (siehe handleLoginSubmit). Vor
    // registerUser setzen, damit der Schlüssel da ist, wenn das currentUser-Observable emittiert.
    try {
      if (guestConvId) localStorage.setItem('claimedConversationId', guestConvId);
      else localStorage.removeItem('claimedConversationId');
    } catch (e) {}

    try {
      const username = formData.email;
      const registeredUser = await registerUser({
        username,
        email: formData.email,
        password: formData.password,
        loginAfterCreate: true,
        userClass: AdornisUser,
      });

      const normalizedSubjects = Array.isArray(formData.subjects)
        ? formData.subjects.filter(subject => typeof subject === 'string' && subject.trim().length > 0)
        : [];

      const candidateUserId = (registeredUser as any)?._id || username;

      const createProfileMutation = formData.role === 'student' ? createStudentMutation : createTeacherMutation;

      await createProfileMutation(
        candidateUserId,
        formData.firstName,
        formData.lastName,
        formData.school,
        normalizedSubjects,
        formData.email,
        formData.password,
        i18n.language || 'de',
      )();

      clearGuestMessageCount();

      await subscribeCurrentUser();

      if (guestConvId && candidateUserId) {
        try {
          await claimGuestConversationMutation(guestConvId, candidateUserId)();
          clearGuestConversationId();
        } catch (claimErr) {
          logger.error(claimErr, `❌ Migration fehlgeschlagen:`);
        }
      }

      onAuthSuccess?.();
    } catch (error: any) {
      try {
        localStorage.removeItem('claimedConversationId');
      } catch (e) {}
      setAuthError(
        error?.message ||
          t('AuthModal.signupFailed', { defaultValue: 'Registrierung fehlgeschlagen. Bitte versuche es erneut.' }),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!forgotPasswordEmail) return;

    setIsSubmitting(true);
    setAuthError(null);
    try {
      await requestPasswordResetMutation(forgotPasswordEmail.trim())();
      setForgotPasswordSuccess(true);
    } catch (error) {
      setAuthError(
        t('AuthModal.forgotPasswordProblem', { defaultValue: 'Es gab ein Problem. Bitte versuche es später erneut.' }),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleSignIn = () => {
    const guestConversationId = getGuestConversationId();
    const successRedirect = new URL(window.location.href);
    successRedirect.searchParams.delete('guestConversationId');

    if (guestConversationId) {
      successRedirect.searchParams.set('guestConversationId', guestConversationId);
    }

    loginWithOAuth({ successRedirect: successRedirect.toString() });
  };

  const localizedSubjects = ALL_SUBJECTS;

  if (showWaitlist) {
    return (
      <div className="w-full max-w-md bg-white dark:bg-card rounded-2xl shadow-2xl p-8 border border-slate-200 dark:border-slate-700 animate-in fade-in zoom-in-95 duration-300">
        {waitlistSuccess ? (
          <div className="text-center py-6">
            <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="h-8 w-8" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-2">
              {t('AuthModal.waitlistTitle')}
            </h2>
            <p className="text-slate-600 dark:text-slate-400 mb-8 leading-relaxed">{t('AuthModal.waitlistDesc')}</p>
            <Button onClick={() => setShowWaitlist(false)} className="w-full bg-brand-500 text-white">
              {t('AuthModal.backToLogin')}
            </Button>
          </div>
        ) : (
          <div className="text-center">{t('AuthModal.waitlistInactive')}</div>
        )}
      </div>
    );
  }

  if (showForgotPassword) {
    return (
      <div className="w-full max-w-md bg-white dark:bg-card rounded-2xl shadow-2xl p-8 border border-slate-200 dark:border-slate-700 animate-in fade-in zoom-in-95 duration-300">
        <button
          onClick={() => setShowForgotPassword(false)}
          className="flex items-center text-sm text-slate-500 dark:text-slate-400 mb-6 hover:text-brand-500 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> {t('AuthModal.backToLogin')}
        </button>

        {forgotPasswordSuccess ? (
          <div className="text-center py-4">
            <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="h-8 w-8" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-2">
              {t('AuthModal.linkSentTitle')}
            </h2>
            <Button onClick={() => setShowForgotPassword(false)} className="w-full bg-brand-500 text-white py-6 mt-4">
              {t('AuthModal.linkSentDesc')}
            </Button>
          </div>
        ) : (
          <div>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-6">
              {t('AuthModal.forgotPasswordTitle')}
            </h2>
            <form onSubmit={handleForgotPasswordSubmit} className="space-y-4">
              <LabeledInput
                id="forgot-email"
                name="forgotPasswordEmail"
                label={t('AuthModal.emailLabel')}
                type="email"
                value={forgotPasswordEmail}
                onChange={e => setForgotPasswordEmail(e.target.value)}
                required
              />
              {authError && <p className="text-sm text-red-500">{authError}</p>}
              <Button
                type="submit"
                disabled={isSubmitting || !forgotPasswordEmail}
                className="w-full bg-brand-500 text-white py-6"
              >
                {t('AuthModal.requestLink')}
              </Button>
            </form>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="w-full max-w-md bg-white dark:bg-card rounded-2xl shadow-2xl p-8 border border-slate-200 dark:border-slate-700 animate-in fade-in zoom-in-95 duration-300 max-h-[90vh] overflow-y-auto custom-scrollbar">
      <div className="relative text-center mb-6">
        <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100">
          {isLogin ? t('AuthModal.welcomeBack') : t('AuthModal.createAccount')}
        </h2>
        {onContinueAsGuest && (
          <button
            type="button"
            onClick={onContinueAsGuest}
            aria-label={t('AuthModal.continueGuest')}
            className="absolute -right-2 -top-4 p-1 text-slate-300 dark:text-slate-600 hover:text-slate-500 dark:hover:text-slate-400 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {isLogin ? (
        <form onSubmit={handleLoginSubmit} className="space-y-4">
          <LabeledInput
            id="login-email"
            name="loginEmail"
            label={t('AuthModal.emailLabel')}
            type="email"
            value={loginEmail}
            onChange={e => setLoginEmail(e.target.value)}
            required
          />
          <div>
            <LabeledInput
              id="login-password"
              name="loginPassword"
              label={t('AuthModal.passwordLabel')}
              type="password"
              value={loginPassword}
              onChange={e => setLoginPassword(e.target.value)}
              required
            />
            <div className="flex justify-end mt-1.5">
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="text-sm text-brand-500 hover:underline"
              >
                {t('AuthModal.forgotPasswordLink')}
              </button>
            </div>
          </div>
          {authError && <p className="text-sm text-red-500">{authError}</p>}
          <Button type="submit" disabled={isSubmitting} className="w-full bg-brand-500 text-white py-6">
            {t('AuthModal.loginButton')}
          </Button>

          <div className="relative mt-0 mb-2">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-white px-3 text-xs text-slate-400">or</span>
            </div>
          </div>

          <button
            type="button"
            onClick={handleGoogleSignIn}
            className="flex w-full items-center justify-center gap-3 rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
          >
            <img src={GoogleIconPath} alt="Google" className="h-5 w-5" />
            Continue with Google
          </button>
        </form>
      ) : (
        <form onSubmit={handleSignUpSubmit} className="space-y-3">
          <div>
            <label className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-1.5 block">
              {t('AuthModal.roleLabel')}
            </label>
            <div className="grid grid-cols-2 gap-2">
              {(['teacher', 'student'] as const).map(role => (
                <button
                  key={role}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, role }))}
                  className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                    formData.role === role
                      ? 'bg-brand-strong text-white border-brand-strong'
                      : 'bg-white dark:bg-card text-slate-700 dark:text-slate-300 border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800'
                  }`}
                >
                  {t(role === 'teacher' ? 'AuthModal.roleTeacher' : 'AuthModal.roleStudent')}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <LabeledInput
              id="firstName"
              name="firstName"
              label={t('AuthModal.firstNameLabel')}
              value={formData.firstName}
              onChange={handleSignUpChange}
              required
              showValidation={showValidation}
            />
            <LabeledInput
              id="lastName"
              name="lastName"
              label={t('AuthModal.lastNameLabel')}
              value={formData.lastName}
              onChange={handleSignUpChange}
              required
              showValidation={showValidation}
            />
          </div>
          <LabeledInput
            id="school"
            name="school"
            label={t('AuthModal.schoolLabel')}
            value={formData.school}
            onChange={handleSignUpChange}
            required
            showValidation={showValidation}
          />
          {formData.role === 'teacher' && (
            <MultiSelectDropdown
              id="subjects"
              label={t('AuthModal.subjectsLabel')}
              options={localizedSubjects}
              selectedValues={formData.subjects}
              onToggle={handleSubjectToggle}
              required
              showValidation={showValidation}
              formatOption={subject => t(`Subjects.${subject}`)}
            />
          )}
          <LabeledInput
            id="email"
            name="email"
            label={t('AuthModal.emailLabel')}
            type="email"
            value={formData.email}
            onChange={handleSignUpChange}
            required
            showValidation={showValidation}
          />
          <LabeledInput
            id="password"
            name="password"
            label={t('AuthModal.passwordLabel')}
            type="password"
            value={formData.password}
            onChange={handleSignUpChange}
            required
            showValidation={showValidation}
          />
          <LabeledInput
            id="confirmPassword"
            name="confirmPassword"
            label={t('AuthModal.confirmPasswordLabel')}
            type="password"
            value={formData.confirmPassword}
            onChange={handleSignUpChange}
            required
            showValidation={showValidation}
          />
          {authError && <p className="text-sm text-red-500">{authError}</p>}
          <Button type="submit" disabled={isSubmitting} className="w-full bg-brand-500 text-white py-6">
            {t('AuthModal.signupButton')}
          </Button>

          <div className="relative mt-0 mb-2">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-white px-3 text-xs text-slate-400">or</span>
            </div>
          </div>

          <button
            type="button"
            onClick={handleGoogleSignIn}
            className="flex w-full items-center justify-center gap-3 rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
          >
            <img src={GoogleIconPath} alt="Google" className="h-5 w-5" />
            Continue with Google
          </button>
        </form>
      )}

      <div className="mt-6 text-center text-sm text-slate-600 dark:text-slate-400">
        <button type="button" onClick={toggleMode} className="text-brand-500 font-semibold hover:underline">
          {isLogin ? t('AuthModal.registerHere') : t('AuthModal.loginHere')}
        </button>
      </div>

      {onContinueAsGuest && (
        <div className="mt-6 pt-4 border-t border-slate-100 dark:border-slate-700 text-center">
          <button
            type="button"
            onClick={onContinueAsGuest}
            className="text-sm text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-colors"
          >
            {t('AuthModal.continueGuest')}
          </button>
        </div>
      )}
    </div>
  );
}
